import React, { useMemo, useState } from 'react';
import { DollarSign, TrendingUp, TrendingDown, Plus, Minus, BarChart3, PieChart, Calendar, Target, AlertTriangle } from 'lucide-react';
import { useUserData } from '@/hooks/useUserData';
import { CurrencySelector, getPreferredCurrency } from '@/components/ui/currency-selector';

interface ChartData {
  labels: string[];
  revenue: number[];
  expenses: number[];
  profit: number[];
}

const ROITracker: React.FC = () => {
  const {
    profile,
    roiEntries,
    loading,
    error,
    addROIEntry,
    totalRevenue,
    totalExpenses,
  } = useUserData();

  const [newTransaction, setNewTransaction] = useState({
    type: 'revenue' as 'revenue' | 'expense',
    amount: '',
    category: '',
    description: '',
    date: new Date().toISOString().split('T')[0],
    currency: getPreferredCurrency().code
  });

  const [activeView, setActiveView] = useState<'overview' | 'add' | 'analytics'>('overview');

  const handleAddTransaction = async () => {
    if (!newTransaction.amount || !newTransaction.category || !newTransaction.description) return;

    await addROIEntry({
      type: newTransaction.type,
      amount: parseFloat(newTransaction.amount),
      category: newTransaction.category,
      description: newTransaction.description,
      date: newTransaction.date
    });

    setNewTransaction({
      type: 'revenue',
      amount: '',
      category: '',
      description: '',
      date: new Date().toISOString().split('T')[0],
      currency: getPreferredCurrency().code
    });
    setActiveView('overview');
  };

  // Totals from hook
  const totalProfit = (totalRevenue ?? 0) - (totalExpenses ?? 0);
  const profitMargin = (totalRevenue ?? 0) > 0 ? (totalProfit / (totalRevenue as number)) * 100 : 0;

  // Category breakdown from real data
  const revenueByCategory = useMemo(() => (
    roiEntries
      .filter(t => t.type === 'revenue')
      .reduce((acc, t) => {
        acc[t.category] = (acc[t.category] || 0) + t.amount;
        return acc;
      }, {} as Record<string, number>)
  ), [roiEntries]);

  const expensesByCategory = useMemo(() => (
    roiEntries
      .filter(t => t.type === 'expense')
      .reduce((acc, t) => {
        acc[t.category] = (acc[t.category] || 0) + t.amount;
        return acc;
      }, {} as Record<string, number>)
  ), [roiEntries]);

  // Build 7-day chart from real entries (must be before any early returns to preserve hook order)
  const chartData: ChartData = useMemo(() => {
    const days = Array.from({ length: 7 }, (_, i) => {
      const d = new Date();
      d.setDate(d.getDate() - (6 - i));
      return d;
    });
    const labels = days.map(d => d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }));
    const revenue = days.map(d => {
      const dayStr = d.toISOString().split('T')[0];
      return roiEntries
        .filter(e => e.type === 'revenue' && (e.date ?? e.created_at).startsWith(dayStr))
        .reduce((s, e) => s + e.amount, 0);
    });
    const expenses = days.map(d => {
      const dayStr = d.toISOString().split('T')[0];
      return roiEntries
        .filter(e => e.type === 'expense' && (e.date ?? e.created_at).startsWith(dayStr))
        .reduce((s, e) => s + e.amount, 0);
    });
    const profit = revenue.map((r, i) => r - expenses[i]);
    return { labels, revenue, expenses, profit };
  }, [roiEntries]);

  if (loading) {
    return (
      <div className="p-6">
        <div className="max-w-7xl mx-auto">
          <div className="animate-pulse space-y-6">
            <div className="h-8 bg-startup-slate rounded w-1/3"></div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="h-24 bg-startup-slate rounded"></div>
              ))}
            </div>
            <div className="h-96 bg-startup-slate rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="max-w-7xl mx-auto">
          <div className="card-startup border-startup-red bg-startup-red/5 text-center py-8">
            <AlertTriangle className="w-12 h-12 text-startup-red mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-startup-red mb-2">Error Loading Data</h3>
            <p className="text-startup-gray">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  // Gate by idea validation if available (route also wraps with IdeaValidationGate)
  const revenueCategories = ['Product Sales', 'Consulting', 'Affiliate', 'Other'];
  const expenseCategories = ['Marketing', 'Tools', 'Operations', 'Development', 'Other'];

  return (
    <div className="p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="animate-fade-in">
          <h1 className="text-3xl font-bold text-startup-navy mb-2 flex items-center">
            <DollarSign className="w-8 h-8 mr-3 text-startup-gold" />
            ROI Tracker 📊
          </h1>
          <p className="text-startup-gray">
            Track your revenue, expenses, and profitability in real-time
          </p>
        </div>

        {/* Navigation */}
        <div className="flex space-x-1 bg-white rounded-2xl p-2 shadow-neumorphic animate-fade-in">
          {[
            { id: 'overview', label: 'Overview', icon: BarChart3 },
            { id: 'add', label: 'Add Transaction', icon: Plus },
            { id: 'analytics', label: 'Analytics', icon: PieChart }
          ].map((tab, index) => (
            <button
              key={tab.id}
              onClick={() => setActiveView(tab.id as any)}
              className={`flex items-center space-x-2 px-6 py-3 rounded-xl transition-all duration-300 flex-1 justify-center ${
                activeView === tab.id
                  ? 'bg-startup-teal text-white shadow-lg'
                  : 'text-startup-gray hover:text-startup-navy hover:bg-startup-slate'
              } animate-fade-in`}
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              <tab.icon className="w-5 h-5" />
              <span className="font-medium">{tab.label}</span>
            </button>
          ))}
        </div>

        {/* Overview */}
        {activeView === 'overview' && (
          <div className="space-y-6">
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 animate-fade-in">
              <div className="card-startup text-center bg-gradient-teal-gold text-white">
                <TrendingUp className="w-8 h-8 mx-auto mb-2" />
                <div className="text-2xl font-bold">${(totalRevenue ?? 0).toLocaleString()}</div>
                <div className="text-white/80 text-sm">Total Revenue</div>
              </div>
              
              <div className="card-startup text-center bg-startup-red/10 border-2 border-startup-red">
                <TrendingDown className="w-8 h-8 mx-auto mb-2 text-startup-red" />
                <div className="text-2xl font-bold text-startup-red">${(totalExpenses ?? 0).toLocaleString()}</div>
                <div className="text-startup-gray text-sm">Total Expenses</div>
              </div>
              
              <div className={`card-startup text-center ${totalProfit >= 0 ? 'bg-green-50 border-2 border-green-200' : 'bg-red-50 border-2 border-red-200'}`}>
                <DollarSign className={`w-8 h-8 mx-auto mb-2 ${totalProfit >= 0 ? 'text-green-600' : 'text-red-600'}`} />
                <div className={`text-2xl font-bold ${totalProfit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  ${totalProfit.toLocaleString()}
                </div>
                <div className="text-startup-gray text-sm">Net Profit</div>
              </div>
              
              <div className="card-startup text-center">
                <Target className="w-8 h-8 mx-auto mb-2 text-startup-navy" />
                <div className="text-2xl font-bold text-startup-navy">{profitMargin.toFixed(1)}%</div>
                <div className="text-startup-gray text-sm">Profit Margin</div>
              </div>
            </div>

            {/* Chart Placeholder */}
            <div className="card-startup animate-fade-in">
              <h3 className="text-xl font-semibold text-startup-navy mb-6 flex items-center">
                <BarChart3 className="w-6 h-6 mr-3 text-startup-teal" />
                Revenue & Expenses Trend
              </h3>
              
              <div className="h-64 bg-startup-slate rounded-xl p-6 flex items-end justify-between space-x-2">
                {chartData.labels.map((label, index) => (
                  <div key={label} className="flex-1 flex flex-col items-center space-y-2">
                    <div className="flex flex-col items-center space-y-1 w-full">
                      {/* Revenue Bar */}
                      <div 
                        className="bg-startup-teal rounded-t w-full transition-all duration-1000 animate-fade-in"
                        style={{ 
                          height: `${(chartData.revenue[index] / Math.max(1, ...chartData.revenue, ...chartData.expenses)) * 120}px`,
                          animationDelay: `${index * 0.1}s`
                        }}
                      />
                      {/* Expense Bar */}
                      <div 
                        className="bg-startup-red rounded-b w-full transition-all duration-1000 animate-fade-in"
                        style={{ 
                          height: `${(chartData.expenses[index] / Math.max(1, ...chartData.revenue, ...chartData.expenses)) * 120}px`,
                          animationDelay: `${index * 0.1 + 0.5}s`
                        }}
                      />
                    </div>
                    <span className="text-xs text-startup-gray">{label}</span>
                  </div>
                ))}
              </div>
              
              <div className="flex justify-center space-x-6 mt-4">
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 bg-startup-teal rounded-full" />
                  <span className="text-startup-gray text-sm">Revenue</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 bg-startup-red rounded-full" />
                  <span className="text-startup-gray text-sm">Expenses</span>
                </div>
              </div>
            </div>

            {/* Recent Transactions */}
            <div className="card-startup animate-fade-in">
              <h3 className="text-xl font-semibold text-startup-navy mb-6">Recent Transactions</h3>
              
              <div className="space-y-3">
                {roiEntries.length > 0 ? roiEntries.slice(0, 5).map((transaction, index) => (
                  <div 
                    key={transaction.id}
                    className="flex items-center justify-between p-4 bg-startup-slate rounded-xl animate-fade-in"
                    style={{ animationDelay: `${index * 0.1}s` }}
                  >
                    <div className="flex items-center space-x-4">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        transaction.type === 'revenue' ? 'bg-startup-teal/10' : 'bg-startup-red/10'
                      }`}>
                        {transaction.type === 'revenue' ? (
                          <Plus className={`w-5 h-5 text-startup-teal`} />
                        ) : (
                          <Minus className={`w-5 h-5 text-startup-red`} />
                        )}
                      </div>
                      <div>
                        <p className="font-medium text-startup-navy">{transaction.description}</p>
                        <p className="text-startup-gray text-sm">{transaction.category} • {new Date(transaction.date || (transaction as any).created_at).toLocaleDateString()}</p>
                      </div>
                    </div>
                    <div className={`text-lg font-semibold ${
                      transaction.type === 'revenue' ? 'text-startup-teal' : 'text-startup-red'
                    }`}>
                      {transaction.type === 'revenue' ? '+' : '-'}${transaction.amount.toLocaleString()}
                    </div>
                  </div>
                )) : (
                  <div className="text-center py-8 text-startup-gray">
                    <DollarSign className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p className="font-medium mb-1">No transactions yet!</p>
                    <p className="text-sm">Add your first transaction to start tracking your ROI.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Add Transaction */}
        {activeView === 'add' && (
          <div className="card-startup animate-fade-in">
            <h2 className="text-2xl font-semibold text-startup-navy mb-6 flex items-center">
              <Plus className="w-6 h-6 mr-3 text-startup-teal" />
              Add New Transaction
            </h2>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="space-y-6">
                {/* Transaction Type */}
                <div>
                  <label className="block text-startup-navy font-medium mb-3">Transaction Type</label>
                  <div className="flex space-x-4">
                    <button
                      onClick={() => setNewTransaction(prev => ({ ...prev, type: 'revenue' }))}
                      className={`flex-1 p-4 rounded-xl border-2 transition-all duration-300 ${
                        newTransaction.type === 'revenue'
                          ? 'border-startup-teal bg-startup-teal/10 text-startup-teal'
                          : 'border-startup-gray/20 text-startup-gray hover:border-startup-teal'
                      }`}
                    >
                      <TrendingUp className="w-6 h-6 mx-auto mb-2" />
                      <div className="font-medium">Revenue</div>
                    </button>
                    <button
                      onClick={() => setNewTransaction(prev => ({ ...prev, type: 'expense' }))}
                      className={`flex-1 p-4 rounded-xl border-2 transition-all duration-300 ${
                        newTransaction.type === 'expense'
                          ? 'border-startup-red bg-startup-red/10 text-startup-red'
                          : 'border-startup-gray/20 text-startup-gray hover:border-startup-red'
                      }`}
                    >
                      <TrendingDown className="w-6 h-6 mx-auto mb-2" />
                      <div className="font-medium">Expense</div>
                    </button>
                  </div>
                </div>

                {/* Currency Selection */}
                <div>
                  <label className="block text-startup-navy font-medium mb-2">Currency</label>
                  <CurrencySelector
                    value={newTransaction.currency}
                    onValueChange={(currency) => setNewTransaction(prev => ({ ...prev, currency }))}
                  />
                </div>

                {/* Amount */}
                <div>
                  <label className="block text-startup-navy font-medium mb-2">Amount</label>
                  <input
                    type="number"
                    value={newTransaction.amount}
                    onChange={(e) => setNewTransaction(prev => ({ ...prev, amount: e.target.value }))}
                    className="w-full px-4 py-3 border-2 border-startup-teal/20 rounded-xl focus:border-startup-teal focus:outline-none"
                    placeholder="0.00"
                  />
                </div>

                {/* Category */}
                <div>
                  <label className="block text-startup-navy font-medium mb-2">Category</label>
                  <select
                    value={newTransaction.category}
                    onChange={(e) => setNewTransaction(prev => ({ ...prev, category: e.target.value }))}
                    className="w-full p-3 border-2 border-startup-teal/20 rounded-xl focus:border-startup-teal focus:outline-none"
                  >
                    <option value="">Select category</option>
                    {(newTransaction.type === 'revenue' ? revenueCategories : expenseCategories).map(category => (
                      <option key={category} value={category}>{category}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="space-y-6">
                {/* Description */}
                <div>
                  <label className="block text-startup-navy font-medium mb-2">Description</label>
                  <textarea
                    value={newTransaction.description}
                    onChange={(e) => setNewTransaction(prev => ({ ...prev, description: e.target.value }))}
                    className="w-full p-3 border-2 border-startup-teal/20 rounded-xl focus:border-startup-teal focus:outline-none resize-none h-24"
                    placeholder="Brief description of the transaction..."
                  />
                </div>

                {/* Date */}
                <div>
                  <label className="block text-startup-navy font-medium mb-2">Date</label>
                  <div className="relative">
                    <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-startup-gray" />
                    <input
                      type="date"
                      value={newTransaction.date}
                      onChange={(e) => setNewTransaction(prev => ({ ...prev, date: e.target.value }))}
                      className="w-full pl-10 pr-4 py-3 border-2 border-startup-teal/20 rounded-xl focus:border-startup-teal focus:outline-none"
                    />
                  </div>
                </div>

                {/* Submit Button */}
                <button
                  onClick={handleAddTransaction}
                  disabled={!newTransaction.amount || !newTransaction.category || !newTransaction.description}
                  className="btn-startup-gold w-full py-4 text-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Add Transaction
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Analytics */}
        {activeView === 'analytics' && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Revenue Breakdown */}
              <div className="card-startup animate-fade-in">
                <h3 className="text-xl font-semibold text-startup-navy mb-6 flex items-center">
                  <PieChart className="w-6 h-6 mr-3 text-startup-teal" />
                  Revenue by Category
                </h3>
                
                <div className="space-y-4">
                  {Object.entries(revenueByCategory).map(([category, amount], index) => (
                    <div key={category} className="animate-fade-in" style={{ animationDelay: `${index * 0.1}s` }}>
                      <div className="flex justify-between mb-2">
                        <span className="text-startup-navy font-medium">{category}</span>
                        <span className="text-startup-teal font-semibold">${amount.toLocaleString()}</span>
                      </div>
                      <div className="w-full bg-startup-slate rounded-full h-2">
                        <div 
                          className="bg-startup-teal rounded-full h-2 transition-all duration-1000"
                          style={{ width: `${(amount / Math.max(1, totalRevenue)) * 100}%` }}
                        />
                      </div>
                      <div className="text-right text-startup-gray text-sm mt-1">
                        {((amount / Math.max(1, totalRevenue)) * 100).toFixed(1)}%
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Expense Breakdown */}
              <div className="card-startup animate-fade-in">
                <h3 className="text-xl font-semibold text-startup-navy mb-6 flex items-center">
                  <PieChart className="w-6 h-6 mr-3 text-startup-red" />
                  Expenses by Category
                </h3>
                
                <div className="space-y-4">
                  {Object.entries(expensesByCategory).map(([category, amount], index) => (
                    <div key={category} className="animate-fade-in" style={{ animationDelay: `${index * 0.1}s` }}>
                      <div className="flex justify-between mb-2">
                        <span className="text-startup-navy font-medium">{category}</span>
                        <span className="text-startup-red font-semibold">${amount.toLocaleString()}</span>
                      </div>
                      <div className="w-full bg-startup-slate rounded-full h-2">
                        <div 
                          className="bg-startup-red rounded-full h-2 transition-all duration-1000"
                          style={{ width: `${(amount / Math.max(1, totalExpenses)) * 100}%` }}
                        />
                      </div>
                      <div className="text-right text-startup-gray text-sm mt-1">
                        {((amount / Math.max(1, totalExpenses)) * 100).toFixed(1)}%
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Cashpurr Insights */}
            <div className="card-startup-dark animate-fade-in relative overflow-hidden">
              <div className="relative z-10">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-startup-gold rounded-full flex items-center justify-center mr-4">
                    <span className="text-2xl">🐱</span>
                  </div>
                  <div>
                    <h3 className="font-semibold text-white">Cashpurr's Financial Insights</h3>
                    <p className="text-white/70 text-sm">AI-powered money analysis</p>
                  </div>
                </div>
                
                <div className="bg-white/10 rounded-xl p-4">
                  <p className="text-white/90 text-sm leading-relaxed mb-4">
                    "Purr-fect profit margins! 🎉 Your {profitMargin.toFixed(1)}% profit margin is looking healthy. 
                    I notice your marketing spend is generating good returns - keep investing in what works! 
                    Consider diversifying your revenue streams for even more meow-ney stability."
                  </p>
                  
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div>
                      <div className="text-lg font-bold text-startup-gold">{totalRevenue > totalExpenses ? '📈' : '📉'}</div>
                      <div className="text-white/70 text-xs">Trend</div>
                    </div>
                    <div>
                      <div className="text-lg font-bold text-startup-gold">
                        {profitMargin > 20 ? 'A+' : profitMargin > 10 ? 'B+' : 'C'}
                      </div>
                      <div className="text-white/70 text-xs">Grade</div>
                    </div>
                    <div>
                      <div className="text-lg font-bold text-startup-gold">💰</div>
                      <div className="text-white/70 text-xs">Potential</div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="absolute -right-4 -bottom-4 opacity-10">
                <div className="w-24 h-24 bg-startup-gold rounded-full animate-float" />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ROITracker;
